import _ from 'lodash'
import Guard from 'arg-guard'

import Config from '../app.config'
import MathTools from '../helpers/math-tools'

import RENOVATION_STATUS from './RENOVATION_STATUS'

const validStatuses = Object.values(RENOVATION_STATUS)

/**
 * Defined structure of a Renovation.
 * A Renovation represents the database stored model for a Renovation plan.
 * It is distinct from a Project in that the Project model contains transient information to drive
 * the builder, such as having all improvements available for the scheme, tagged as recommended or
 * selected, whereas a Renovation only contains the selected improvements.
 * @export Renovation
 * @class Renovation
 */
export default class Renovation {

    /**
     * Creates an instance of a Renovation.
     * @param {RenovationAsJson|RenovationSourceData|Renovation} data Source data - From API or state.
     * @param {number|string|null} addressId Optional address identifier - only required if not present in the data parameter as a property.
     * @param {?string} name An optional specific name for the renovation plan.
     * @memberof Renovation
     */
    constructor(data, addressId = null, name = null) {
        Guard.throwIf(data, 'data')

        addressId = addressId || data.addressId
        Guard.throwIf(addressId, 'addressId')

        /** @type {number|null} **/
        this.id = data.id ? Number(data.id) : null

        /** @type {number|null} **/
        this.schemeId = data.schemeId ? Number(data.schemeId) : null

        /** @type {number|null} **/
        this.servicedSchemeId = data.servicedSchemeId ? Number(data.servicedSchemeId) : null

        /** @type {number} **/
        this.addressId = Number(addressId)

        /** @type {string} **/
        this.addressMain = data.addressMain

        /** @type {string} **/
        this.postcode = data.postcode

        /** @type {string} **/
        this.name = name !== null ? name : data.name
        this._status = data.status || RENOVATION_STATUS.planning

        this._budget = data.budget === Config.serverMaxBudget ? Number.MAX_VALUE : data.budget
        this._cost = Number(data.cost)

        /** @type {Array<Improvement>|Array<{id, name}>} **/
        this.improvements = data.improvements ? data.improvements
            : data.selectedImprovements ? data.selectedImprovements : []
        this.improvementCount = data.improvementCount ? Number(data.improvementCount) : this.improvements.length

        /** @type {Array<Note>} **/
        this.notes = data.notes || []

        /** @type {Array<ServiceOffering>} **/
        this.services = data.services || []

        /** @type {string} **/
        this.budget = this._budget < Config.serverMaxBudget ? MathTools.segmentCurrency(this._budget) : 'Unlimited'

        /** @type {string} **/
        this.cost = this._cost ? MathTools.segmentCurrency(this._cost) : null

        /** @type {string} **/
        this.motivation = data.motivation ? data.motivation : 'unknown'
    }

    /**
     * The default renovation name to use when there is only a single plan (My Renovation).
     * @type {string}
     */
    static DEFAULT_NAME = 'My Renovation'

    /**
     * The renovation name to use when there are multiples.
     * @param {number|string} iteration The end of the plan name (11, 12, 13).
     * @type {string}
     */
    static templatedBasicName(iteration) {
        return `Renovation ${ iteration }`
    }

    /**
     * The renovation name to use when there are multiples.
     * @param {string} iteration The middle of the plan name (Second, Third etc.).
     * @type {string}
     */
    static templatedFriendlyName(iteration) {
        return `My ${ iteration } Renovation`
    }

    /**
     * The UI friendly display status string for the renovation.
     * @readonly
     * @returns {string}
     * @memberof Renovation
     */
    get displayStatus() {
        return _.capitalize(this.status)
    }

    /**
     * A Logging friendly string of the plan identification parameters.
     * @returns {string}
     */
    get loggingString() {
        return `Address: ${ this.addressId } - ${ this.name } for ${ this.motivation } at ${ this.cost } [${ this.displayStatus }]`
    }

    /**
     * The effective scheme identifier (Serviced if available, else standard).
     * @readonly
     * @returns {number}
     * @memberof Renovation
     */
    get effectiveSchemeId() {
        return this.servicedSchemeId || this.schemeId
    }

    // noinspection JSUnusedGlobalSymbols
    /**
     * Gets a flag to indicate if the status is beyond planning.
     * @readonly
     * @returns {boolean}
     * @memberof Renovation
     */
    get isPastPlanning() {
        return this._status !== RENOVATION_STATUS.planning
    }

    /**
     * Gets the status value.
     * @readonly
     * @returns {string}
     * @memberof Renovation
     */
    get status() {
        return this._status
    }

    /**
     * Sets the status value, limited by valid options.
     * @param {string} value
     * @memberof Renovation
     */
    set status(value) {
        if (this._status === RENOVATION_STATUS.updating && value !== RENOVATION_STATUS.submitted) return
        if (value === '' || value === 'unknown') value = RENOVATION_STATUS.planning
        if (value && !validStatuses.includes(value))
            throw new Error('Invalid status provided for the renovation.')

        this._status = value
    }

    /**
     * Returns the object in a form suitable to store.
     * @returns {RenovationAsJson}
     * @memberof Renovation
     */
    toStoreJson() {
        return {
            id: this.id,
            schemeId: this.schemeId,
            servicedSchemeId: this.servicedSchemeId,
            addressId: this.addressId,
            name: this.name,
            status: this.status,
            budget: this._budget,
            cost: this._cost,
            motivation: this.motivation,
            improvements: this.improvements,
            improvementCount: this.improvementCount,
        }
    }

    /**
     * Ensures that the provided data is already of this type. If not, it runs the constructor.
     * @param {Object} data Source data - From API or state.
     * @param {?number} addressId Optional address identifier - If present as a data parameter the data parameter takes precedence.
     * @param {string} name Optional name for the renovation - If present as a data parameter the data parameter takes precedence.
     * @returns {Renovation} Either the data is passed back if already of this type or the result of the constructor is.
     * @memberof Renovation
     */
    static ensure(data, addressId = null, name = Renovation.DEFAULT_NAME) {
        return data && data instanceof Renovation
            ? data
            : data
                ? new Renovation(data, data.addressId ? data.addressId : addressId, data.name ? data.name : name)
                : new Renovation(null) // This will throw.
    }

    /**
     * Returns the object type from raw stored JSON data.
     * @param {?Object} data The stored data.
     * @memberof Renovation
     */
    static fromStoreJson(data = null) {
        return data ? new Renovation(data) : null
    }
}
